/**
 * Phase 3 dual-write helper. Mirrors the current state of an order (and any
 * child rows, if multi-leg) into the new `legs` and `trips` tables by calling
 * the SECURITY DEFINER Postgres helpers `sync_order_to_legs` and
 * `sync_order_trip`.
 *
 * These helpers are idempotent. They never throw — failures log a warning so
 * the legacy `orders` write path remains the source of truth during migration.
 * Once read switchover (Phase 4) lands, we can promote sync errors to hard
 * failures.
 */
import { supabase } from "@/integrations/supabase/client";

/** Minimal shape of an active row in `public.trips` for an order's legs. */
export interface ActiveLegTrip {
  trip_row_id: string;
  leg_id: string;
  leg_sequence: number;
  eapi_trip_id: string;
  assigned_tractor_id: string | null;
}

/** Trip row + supersession metadata, used by the cancel fan-out backstop. */
export interface AnyLegTrip extends ActiveLegTrip {
  is_active: boolean;
  superseded_reason: string | null;
}

/** Persisted leg row fields needed to build an EAPI CreateTrip body for that leg. */
export interface LegRow {
  id: string;
  tenant_id: string;
  leg_sequence: number;
  ship_from_street_address: string;
  ship_from_city: string;
  ship_from_province_state: string | null;
  ship_from_postal_zip_code: string | null;
  ship_from_country: string | null;
  ship_to_street_address: string;
  ship_to_city: string;
  ship_to_province_state: string | null;
  ship_to_postal_zip_code: string | null;
  ship_to_country: string | null;
  ready_time: string;
  ready_time_end: string | null;
  requested_delivery_time: string;
  requested_delivery_time_end: string | null;
  assigned_tractor_id: string | null;
  assigned_operator_id: string | null;
  trailer_type_id: string | null;
}

/**
 * List active EAPI trips attached to any leg of the given order. Used by the
 * per-leg cancel fan-out and per-leg Trip Pivot logic during the dual-write
 * window. Best-effort: returns [] on error.
 */
export async function listActiveLegTripsForOrder(orderId: string): Promise<ActiveLegTrip[]> {
  if (!orderId) return [];
  const { data, error } = await supabase
    .from("trips" as any)
    .select("id, leg_id, eapi_trip_id, legs!inner(order_id, leg_sequence, assigned_tractor_id)")
    .eq("is_active", true)
    .eq("legs.order_id", orderId);
  if (error) {
    console.warn("[leg-trip-sync] listActiveLegTripsForOrder failed", orderId, error);
    return [];
  }
  return ((data as any[]) || [])
    .filter((r) => !!r?.eapi_trip_id)
    .map((r) => ({
      trip_row_id: r.id as string,
      leg_id: r.leg_id as string,
      leg_sequence: r.legs?.leg_sequence as number,
      eapi_trip_id: r.eapi_trip_id as string,
      assigned_tractor_id: (r.legs?.assigned_tractor_id as string | null) ?? null,
    }));
}

/**
 * List every `trips` row attached to any leg of `orderId`, regardless of
 * `is_active`. Used by the order-level cancel fan-out as a backstop so
 * orphaned-but-not-cancelled trips (left over from earlier pivots / clears)
 * still get cancelled upstream. Bounded to the last `sinceHours` (default
 * 30 days) to avoid re-cancelling ancient rows. Best-effort: returns [] on
 * error.
 */
export async function listAllLegTripsForOrder(
  orderId: string,
  opts: { sinceHours?: number } = {},
): Promise<AnyLegTrip[]> {
  if (!orderId) return [];
  const sinceHours = opts.sinceHours ?? 24 * 30;
  const sinceIso = new Date(Date.now() - sinceHours * 3600 * 1000).toISOString();
  const { data, error } = await supabase
    .from("trips" as any)
    .select(
      "id, leg_id, eapi_trip_id, is_active, superseded_reason, created_at, legs!inner(order_id, leg_sequence, assigned_tractor_id)",
    )
    .eq("legs.order_id", orderId)
    .gte("created_at", sinceIso);
  if (error) {
    console.warn("[leg-trip-sync] listAllLegTripsForOrder failed", orderId, error);
    return [];
  }
  return ((data as any[]) || [])
    .filter((r) => !!r?.eapi_trip_id)
    .map((r) => ({
      trip_row_id: r.id as string,
      leg_id: r.leg_id as string,
      leg_sequence: r.legs?.leg_sequence as number,
      eapi_trip_id: r.eapi_trip_id as string,
      assigned_tractor_id: (r.legs?.assigned_tractor_id as string | null) ?? null,
      is_active: !!r.is_active,
      superseded_reason: (r.superseded_reason as string | null) ?? null,
    }));
}

/**
 * Fetch a single persisted leg row by parent order + leg_sequence. Returns
 * null if the leg has not been mirrored yet (caller should run
 * `syncOrderToLegs` first).
 */
export async function fetchLegRow(
  orderId: string,
  legSequence: number,
): Promise<LegRow | null> {
  if (!orderId || !legSequence) return null;
  const { data, error } = await supabase
    .from("legs" as any)
    .select(
      "id, tenant_id, leg_sequence, ship_from_street_address, ship_from_city, ship_from_province_state, ship_from_postal_zip_code, ship_from_country, ship_to_street_address, ship_to_city, ship_to_province_state, ship_to_postal_zip_code, ship_to_country, ready_time, ready_time_end, requested_delivery_time, requested_delivery_time_end, assigned_tractor_id, assigned_operator_id, trailer_type_id",
    )
    .eq("order_id", orderId)
    .eq("leg_sequence", legSequence)
    .maybeSingle();
  if (error) {
    console.warn("[leg-trip-sync] fetchLegRow failed", orderId, legSequence, error);
    return null;
  }
  return ((data as unknown) as LegRow) ?? null;
}

/**
 * Result of a trip-mirror write. Callers that must not silently lose an
 * external trip id (e.g. Regenerate External Trip) should check `ok`; legacy
 * best-effort callers can keep ignoring the return value.
 */
export interface LegTripWriteResult {
  ok: boolean;
  /** Human-readable failure reason when `ok === false`. */
  error?: string;
}

/**
 * Insert a new active row into `public.trips` for a leg after a successful
 * EAPI createTrip. Still logs a warning on failure, but now also reports the
 * failure so callers can surface it instead of showing a false success.
 */
export async function insertActiveLegTrip(params: {
  legId: string;
  tenantId: string;
  eapiTripId: string;
}): Promise<LegTripWriteResult> {
  const { legId, tenantId, eapiTripId } = params;
  if (!legId || !tenantId || !eapiTripId) {
    return { ok: false, error: "Missing leg, tenant, or external trip id" };
  }
  const { error } = await supabase.from("trips" as any).insert({
    leg_id: legId,
    tenant_id: tenantId,
    eapi_trip_id: eapiTripId,
    is_active: true,
  });
  if (error) {
    console.warn("[leg-trip-sync] insertActiveLegTrip failed", legId, eapiTripId, error);
    return { ok: false, error: error.message ?? String(error) };
  }
  return { ok: true };
}

/**
 * Mark a `trips` row as superseded. Used after we successfully cancel an EAPI
 * trip externally so the local mirror reflects the new state without waiting
 * for the next `sync_order_trip` invocation.
 */
export async function supersedeLegTrip(
  tripRowId: string,
  reason: string,
): Promise<LegTripWriteResult> {
  if (!tripRowId) return { ok: false, error: "Missing trip row id" };
  const { error } = await supabase
    .from("trips" as any)
    .update({
      is_active: false,
      superseded_at: new Date().toISOString(),
      superseded_reason: reason,
    })
    .eq("id", tripRowId);
  if (error) {
    console.warn("[leg-trip-sync] supersedeLegTrip failed", tripRowId, error);
    return { ok: false, error: error.message ?? String(error) };
  }
  return { ok: true };
}

/**
 * Resolve which leg an active external trip id belongs to on a given order.
 * Used so a regenerate initiated from the parent view still persists against
 * the correct leg instead of falling back to the order-level column.
 */
export async function findLegIdByActiveTripId(
  orderId: string,
  eapiTripId: string,
): Promise<string | null> {
  if (!orderId || !eapiTripId) return null;
  const active = await listActiveLegTripsForOrder(orderId);
  return active.find((t) => t.eapi_trip_id === eapiTripId)?.leg_id ?? null;
}

/**
 * Bump `updated_at` on an active `trips` row after a successful EAPI PATCH
 * so downstream stale-detection (which compares the local mirror timestamp
 * to the order/leg's last edit) has a reliable signal. Best-effort.
 */
export async function touchActiveLegTrip(tripRowId: string): Promise<void> {
  if (!tripRowId) return;
  const { error } = await supabase
    .from("trips" as any)
    .update({ updated_at: new Date().toISOString() })
    .eq("id", tripRowId);
  if (error) {
    console.warn("[leg-trip-sync] touchActiveLegTrip failed", tripRowId, error);
  }
}

export async function syncOrderToLegs(orderId: string): Promise<void> {
  if (!orderId) return;
  const { error } = await supabase.rpc("sync_order_to_legs" as any, { _order_id: orderId });
  if (error) {
    console.warn("[leg-trip-sync] sync_order_to_legs failed", orderId, error);
  }
}

export async function syncOrderTrip(orderId: string): Promise<void> {
  if (!orderId) return;
  const { error } = await supabase.rpc("sync_order_trip" as any, { _order_id: orderId });
  if (error) {
    console.warn("[leg-trip-sync] sync_order_trip failed", orderId, error);
  }
}

/** Convenience: run both helpers in sequence. Trip sync depends on legs existing. */
export async function syncOrderLegsAndTrip(orderId: string): Promise<void> {
  await syncOrderToLegs(orderId);
  await syncOrderTrip(orderId);
}

/** Sync many orders (e.g. recurring batch, CSV import). Best-effort; never throws. */
export async function syncOrdersByIds(orderIds: string[]): Promise<void> {
  await Promise.all(orderIds.filter(Boolean).map((id) => syncOrderLegsAndTrip(id)));
}